import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const statusConfig = {
  SUCCESS: {
    label: "Success",
    bg: "#e6f4ea",
    color: "#1e8e3e",
    border: "#b7e1cd",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
  },
  FAILURE: {
    label: "Failed",
    bg: "#fde8e8",
    color: "#d93025",
    border: "#f5c6cb",
    icon: <HighlightOffIcon sx={{ fontSize: 18 }} />,
  },
  PENDING: {
    label: "Pending",
    bg: "#fff4e5",
    color: "#ed6c02",
    border: "#ffe0b2",
    icon: <AccessTimeIcon sx={{ fontSize: 18 }} />,
  },
  MANDATE_PARTIAL_REG: {
    label: "Initiated",
    bg: "#dbeafe",
    color: "#2563eb",
    border: "#bfdbfe",
    icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />,
  },
  MANDATE_PARTIAL_REG_NO_UMRN: {
    label: "Incomplete",
    bg: "#fef3c7",
    color: "#d97706",
    border: "#fde68a",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  },
};

const getFooterConfig = (item) => {
  if (item.UMRN) {
    return {
      text: `Generated (${item.UMRN})`,
      color: "#16a34a",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#16a34a" }} />,
    };
  }

  if (item.ActionType === "MANDATE_PARTIAL_REG") {
    return {
      text: "Incomplete Registration",
      color: "#f97316",
      icon: <ErrorOutlineIcon sx={{ fontSize: 18, color: "#f97316" }} />,
    };
  }

  return {
    text: "Not Generated",
    color: "#6b7280",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />,
  };
};


const formatDate = (isoDate) => {
  const date = new Date(isoDate);

  // Get UTC time components directly without adjusting for local time zone
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const year = date.getUTCFullYear();

  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // Adjust for 12-hour format

  return `${day} ${month} ${year} · ${hours}:${minutes}:${seconds} ${ampm}`;
};

const EMandateHistoryModal = ({ open, onClose, historyData = [] }) => {
  // Flatten + Sort attempts
  // console.log("historyData:", historyData);

  const flattenedData = useMemo(() => {
    return Array.isArray(historyData) ? historyData : [];
  }, [historyData]);

  // console.log("flattenedData:", flattenedData);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "#f9fafb",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#f9fafb",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              E-Mandate History
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: "#6b7280",
                mt: 0.5,
              }}
            >
              Activity log for mandate registration
            </Typography>
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 3 }}>
          {flattenedData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 16, color: "#6b7280" }}>
                No history available.
              </Typography>
            </Box>
          ) : (
            flattenedData.map((item, index) => {
              // console.log("Processing item:", item);

              let config;

              if (
                item.ActionType === "MANDATE_PARTIAL_REG" && item.UMRN &&
                item.Status === "SUCCESS"
              ) {
                config = statusConfig.MANDATE_PARTIAL_REG;
              } else if (
                item.ActionType === "MANDATE_PARTIAL_REG" && !item.UMRN
              ) {
                config = statusConfig.MANDATE_PARTIAL_REG_NO_UMRN;
              } else if (item.Status === "SUCCESS") {
                config = statusConfig.SUCCESS;
              } else if (item.Status === "FAILURE") {
                config = statusConfig.FAILURE;
              } else {
                config = statusConfig.PENDING;
              }

              return (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: 3,
                    p: 2.5,
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* Date + Status */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}
                    >
                      {formatDate(item.ActionDate)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "20px",
                        backgroundColor: config.bg,
                        color: config.color,
                        border: `1px solid ${config.border}`,
                        fontSize: "1rem",
                        fontWeight: 500,
                      }}
                    >
                      {config.icon}
                      {config.label}
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      mt: 1.5,
                      color: "#111827",
                      letterSpacing: 0.5,
                    }}
                  >
                    {/* {item.title} */}
                    {item.ActionType?.replaceAll("_", " ")}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#4b5563",
                      mt: 0.8,
                      lineHeight: 1.6,
                    }}
                  >
                    {item.LogMessage}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Footer */}
                  <Box display="flex" alignItems="center">
                    {(() => {
                      const footer = getFooterConfig(item);

                      return (
                        <Box display="flex" alignItems="center">
                          {footer.icon}

                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#6b7280",
                              ml: 1,
                              mr: 0.5,
                            }}
                          >
                            Mandate Registration:
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: footer.color,
                            }}
                          >
                            {footer.text}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EMandateHistoryModal;